import { escapeRegExp } from "@redotech/util/string";
import { Fragment, memo, ReactNode, useMemo } from "react";
import { Text } from "../../../text";
import * as styles from "./command-palette-search-view.module.css";

/**
 * Renders `text`, wrapping any substring that matches a whitespace-separated
 * token from `query` in a highlight span. Matching is case-insensitive.
 */
export const HighlightedText = memo(function HighlightedText({
  text,
  query,
}: {
  text: string;
  query: string;
}): ReactNode {
  const segments = useMemo(() => {
    const tokens = query.trim().split(/\s+/).filter(Boolean).map(escapeRegExp);

    if (tokens.length === 0) {
      return [{ text, highlighted: false }];
    }

    const regex = new RegExp(`(${tokens.join("|")})`, "gi");
    // String.split with a capturing group alternates unmatched/matched parts,
    // so odd indices are the captured matches.
    return text
      .split(regex)
      .map((part, index) => ({ text: part, highlighted: index % 2 === 1 }))
      .filter((segment) => segment.text.length > 0);
  }, [text, query]);

  return segments.map((segment, index) =>
    segment.highlighted ? (
      <Text as="span" className={styles.highlight} key={index}>
        {segment.text}
      </Text>
    ) : (
      <Fragment key={index}>{segment.text}</Fragment>
    ),
  );
});
